import { create } from 'apisauce';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { activityRunService } from '../services/runAPI';
import { OkAction, ErrorAction } from './baseActions';
import { Artifact } from '../types/resources';
import { formatApiErrorPayload } from './utils';
import { artifactService } from '~/services/taskDataAPI';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum ArtifactActionType {
  SILENT_LIST_ARTIFACTS = 'SILENT_LIST_ARTIFACTS',
  LIST_ARTIFACTS = 'LIST_ARTIFACTS',
  LIST_ARTIFACTS_OK = 'LIST_ARTIFACTS_OK',
  LIST_ARTIFACTS_ERROR = 'LIST_ARTIFACTS_ERROR',
  GET_ARTIFACT_LINK = 'GET_ARTIFACT_LINK',
  GET_ARTIFACT_LINK_OK = 'GET_ARTIFACT_LINK_OK',
  GET_ARTIFACT_LINK_ERROR = 'GET_ARTIFACT_LINK_ERROR',
  DELETE_ARTIFACTS = 'DELETE_ARTIFACTS',
  DELETE_ARTIFACTS_OK = 'DELETE_ARTIFACTS_OK',
  DELETE_ARTIFACTS_ERROR = 'DELETE_ARTIFACTS_ERROR',
  DELETE_ARTIFACT = 'DELETE_ARTIFACT',
  DELETE_ARTIFACT_OK = 'DELETE_ARTIFACT_OK',
  DELETE_ARTIFACT_ERROR = 'DELETE_ARTIFACT_ERROR',
}

type WithRunId = { runId: string };
type WithArtifactId = { artifactId: string };

type ListArtifactsAction =
  | Action<ArtifactActionType.SILENT_LIST_ARTIFACTS>
  | (Action<ArtifactActionType.LIST_ARTIFACTS> & WithRunId)
  | (OkAction<ArtifactActionType.LIST_ARTIFACTS_OK, Artifact[]> & WithRunId)
  | (ErrorAction<ArtifactActionType.LIST_ARTIFACTS_ERROR> & WithRunId);

type GetArtifactLinkAction =
  | (Action<ArtifactActionType.GET_ARTIFACT_LINK> & WithArtifactId)
  | (OkAction<ArtifactActionType.GET_ARTIFACT_LINK_OK, string> & WithArtifactId)
  | (ErrorAction<ArtifactActionType.GET_ARTIFACT_LINK_ERROR> & WithArtifactId);

type DeleteArtifactsAction =
  | (Action<ArtifactActionType.DELETE_ARTIFACTS> & WithRunId)
  | (OkAction<ArtifactActionType.DELETE_ARTIFACTS_OK, Artifact[]> & WithRunId)
  | (ErrorAction<ArtifactActionType.DELETE_ARTIFACTS_ERROR> & WithRunId);

type DeleteArtifactAction =
  | (Action<ArtifactActionType.DELETE_ARTIFACT> & WithRunId & WithArtifactId)
  | (OkAction<ArtifactActionType.DELETE_ARTIFACT_OK, Artifact> & WithRunId & WithArtifactId)
  | (ErrorAction<ArtifactActionType.DELETE_ARTIFACT_ERROR> & WithRunId & WithArtifactId);

export type ArtifactAction =
  | ListArtifactsAction
  | GetArtifactLinkAction
  | DeleteArtifactsAction
  | DeleteArtifactAction;

export const listArtifacts =
  (
    processId: string,
    processRunId: string,
    runId: string,
  ): ThunkAction<void, AppState, any, ListArtifactsAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.artifacts.byRunId[runId]) {
      dispatch({ type: ArtifactActionType.SILENT_LIST_ARTIFACTS });
    } else {
      dispatch({ type: ArtifactActionType.LIST_ARTIFACTS, runId });
    }
    const workspaceId = selectCurrentWorkspaceId(getState());

    // Fetch link to artifacts
    const response = await activityRunService.getActivityRunArtifactsLink({
      workspaceId,
      processId,
      processRunId,
      activityRunId: runId,
    });
    // Fetch real artifacts
    if (response.ok) {
      const artifactsLink = create({ baseURL: response.data as string });
      const artifactsResponse = await artifactsLink.get('');

      if (artifactsResponse.ok) {
        dispatch({
          type: ArtifactActionType.LIST_ARTIFACTS_OK,
          data: (artifactsResponse.data as { data: Artifact[] }).data,
          runId,
        });
      } else {
        dispatch({
          ...formatApiErrorPayload(artifactsResponse),
          type: ArtifactActionType.LIST_ARTIFACTS_ERROR,
          runId,
        });
      }
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ArtifactActionType.LIST_ARTIFACTS_ERROR,
        runId,
      });
    }
  };

export const getArtifactLink =
  (
    processId: string,
    processRunId: string,
    runId: string,
    artifactId: string,
    artifactName: string,
  ): ThunkAction<Promise<{ ok: boolean; data: string }>, AppState, any, GetArtifactLinkAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ArtifactActionType.GET_ARTIFACT_LINK, artifactId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunArtifactLink({
      workspaceId,
      processId,
      processRunId,
      activityRunId: runId,
      artifactId,
      artifactName,
    });

    if (response.ok) {
      dispatch({
        artifactId,
        type: ArtifactActionType.GET_ARTIFACT_LINK_OK,
        data: response.data as string,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ArtifactActionType.GET_ARTIFACT_LINK_ERROR,
        artifactId,
      });
    }

    return response as { ok: boolean; data: string };
  };

export const deleteArtifacts =
  (runId: string): ThunkAction<void, AppState, any, DeleteArtifactsAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ArtifactActionType.DELETE_ARTIFACTS, runId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await artifactService.deleteArtifacts({ workspaceId, stepRunId: runId });

    if (response.ok) {
      dispatch({
        runId,
        type: ArtifactActionType.DELETE_ARTIFACTS_OK,
        data: response.data as Artifact[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ArtifactActionType.DELETE_ARTIFACTS_ERROR,
        runId,
      });
    }
  };

export const deleteArtifact =
  (runId: string, artifactId: string): ThunkAction<void, AppState, any, DeleteArtifactAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ArtifactActionType.DELETE_ARTIFACT, runId, artifactId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await artifactService.deleteArtifact({
      workspaceId,
      stepRunId: runId,
      artifactId,
    });

    if (response.ok) {
      dispatch({
        runId,
        artifactId,
        type: ArtifactActionType.DELETE_ARTIFACT_OK,
        data: response.data as Artifact,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ArtifactActionType.DELETE_ARTIFACT_ERROR,
        runId,
        artifactId,
      });
    }
  };
